/**
 * nova-avatar.tsx — the core Nova character as pure SVG JSX.
 * Geometry, colors, and layer order are transcribed 1:1 from nova.js;
 * ambient loops (float, blink, confetti, chest door…) come from nova.css
 * via the `expr-*` wrapper class (see nova-expression.tsx).
 */
import * as React from "react";

import { NovaEyes } from "./nova-eyes";
import { NOVA_VIEWBOX, novaLayout } from "./nova-geometry";
import { NOVA_COLORS, shadeHex } from "./nova-palette";
import type { NovaAvatarOptions } from "./nova-types";
import { useActiveSeason } from "@/hooks/useSeasonalThemes";
import { NOVA_OUTFIT } from "@/lib/seasons";

const C = NOVA_COLORS;

export interface NovaAvatarProps extends NovaAvatarOptions {
  /** Rendered width in px (SVG scales from a 460×460 viewBox). */
  width?: number;
  className?: string;
}

export function NovaAvatar({
  width = 240,
  tilt = 0,
  eyeShape = "triUp",
  eyeColor = C.blue,
  eyeBlockColors,
  eyeBright = "soft",
  eyeAsym = false,
  eyeLook = "center",
  cheeks = false,
  cheekColor = C.coral,
  cheekOpacity = 0.5,
  antennaTilt = 0,
  antennaSpin = false,
  antennaGlow = false,
  antennaBallColor = C.orange,
  antennaBallColors,
  bodyColor,
  bodyDarkColor,
  arms = "float",
  confetti = false,
  sleepy = false,
  tear = false,
  stressMark = false,
  bodyGapShrink = 0,
  className,
}: NovaAvatarProps) {
  const uid = React.useId().replace(/:/g, "");
  const L = novaLayout(bodyGapShrink);
  const { head, bezel, screen, body, chest } = L;

  // SEASON-1 — Nova dresses for the season (pumpkin orange for
  // Halloween, evergreen with a red light for Christmas) whenever she's
  // in her own teal; a caller that paints her another colour keeps it.
  // Only inside the app shell — `useActiveSeason()` is null elsewhere.
  const season = useActiveSeason();
  const outfit = season && (bodyColor === undefined || bodyColor === C.body) ? NOVA_OUTFIT[season] : null;
  const bodyFill = outfit ? outfit.body : bodyColor ?? C.body;
  const bodyDarkFill = outfit && bodyDarkColor === undefined ? outfit.bodyDark : bodyDarkColor ?? C.bodyDark;
  const dim = sleepy ? 0.55 : 1;

  // Antenna — stem rises from top-center of head; sleepy adds a 22° droop.
  const stemBaseX = head.x + head.w / 2;
  const stemBaseY = head.y + 4;
  const stemLen = head.h * 0.42;
  const tiltRad = ((antennaTilt + (sleepy ? 22 : 0)) * Math.PI) / 180;
  const tipX = stemBaseX + Math.sin(tiltRad) * stemLen;
  const tipY = stemBaseY - Math.cos(tiltRad) * stemLen;
  const ballR = antennaGlow ? 9 : 7;
  const ringR = ballR + 8;

  const eyes = (
    <NovaEyes
      layout={L}
      shape={eyeShape}
      color={eyeColor}
      bright={eyeBright}
      asym={eyeAsym}
      look={eyeLook}
      dim={dim}
      blockColors={eyeBlockColors}
    />
  );

  // NOVA-LOAD-1 — the rainbow antenna cycle is opt-in and driven from
  // nova.css rather than an inline <style>, because inline SVG style
  // blocks have historically been dropped by the production CSS pass
  // (see the note the old loader left in index.css). Presence of the
  // prop flips the class on; the first colour is the static fallback so
  // a reduced-motion / animation-stripped render still looks deliberate.
  const rainbowBall = Boolean(antennaBallColors?.length);
  const ballFill = rainbowBall
    ? antennaBallColors![0]
    : outfit && antennaBallColor === C.orange
      ? outfit.antennaBall
      : antennaBallColor;

  const tiltCx = head.x + head.w / 2;
  const tiltCy = body.y + body.h / 2 - 40;

  return (
    <svg
      viewBox={`0 0 ${NOVA_VIEWBOX.w} ${NOVA_VIEWBOX.h}`}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Nova"
      className={className}
    >
      <defs>
        <linearGradient id={`bodyG-${uid}`} x1="0.15" y1="0" x2="0.85" y2="1">
          <stop offset="0" stopColor={shadeHex(bodyFill, 0.32)} />
          <stop offset="0.45" stopColor={bodyFill} />
          <stop offset="1" stopColor={shadeHex(bodyFill, -0.34)} />
        </linearGradient>
        <linearGradient id={`chestG-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shadeHex(bodyDarkFill, -0.25)} />
          <stop offset="1" stopColor={shadeHex(bodyDarkFill, 0.12)} />
        </linearGradient>
        <radialGradient id={`scrG-${uid}`} cx="0.5" cy="0.45" r="0.8">
          <stop offset="0" stopColor={C.screen} />
          <stop offset="0.72" stopColor={C.screen} />
          <stop offset="1" stopColor="#060A12" />
        </radialGradient>
        <pattern id={`pxg-${uid}`} width="9" height="9" patternUnits="userSpaceOnUse">
          <path d="M 9 0 L 0 0 0 9" fill="none" stroke="#060A12" strokeWidth="1.6" opacity="0.65" />
        </pattern>
        <filter id={`eglow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      <g transform={`translate(${NOVA_VIEWBOX.xOffset} 0)`}>
        {confetti && <Confetti />}

        {stressMark && (
          <g className="stress-mark">
            <line x1="60" y1="66" x2="82" y2="54" stroke={C.deep} strokeWidth="3" strokeLinecap="round" />
            <line x1="66" y1="86" x2="88" y2="74" stroke={C.deep} strokeWidth="3" strokeLinecap="round" />
          </g>
        )}

        <g
          className="nova-body"
          style={{ transformOrigin: `${tiltCx.toFixed(2)}px ${(body.y + body.h).toFixed(2)}px` }}
          transform={`rotate(${tilt} ${tiltCx} ${tiltCy})`}
        >
          {/* Antenna stem (drawn first so head covers its base) */}
          <line x1={stemBaseX} y1={stemBaseY + 8} x2={tipX} y2={tipY} stroke={C.deep} strokeWidth="2.2" strokeLinecap="round" />
          {/* Orbital ring (behind ball) */}
          <ellipse cx={tipX} cy={tipY} rx={ringR} ry={ringR * 0.36} stroke={C.ivory} strokeWidth="1.6" fill="none" transform={`rotate(-12 ${tipX} ${tipY})`} />
          <circle
            className={`antenna-ball${rainbowBall ? " nova-rainbow-ball" : ""}`}
            cx={tipX}
            cy={tipY}
            r={ballR}
            fill={ballFill}
          />
          <ellipse cx={tipX - ballR * 0.3} cy={tipY - ballR * 0.35} rx={ballR * 0.42} ry={ballR * 0.3} fill="#FFFFFF" opacity="0.55" />
          {/* Front half of the ring, over the ball */}
          <path
            d={`M ${tipX - ringR * 0.98} ${tipY + 0.5} A ${ringR} ${ringR * 0.36} -12 0 0 ${tipX + ringR * 0.98} ${tipY - 2.2}`}
            stroke={C.ivory}
            strokeWidth="1.6"
            fill="none"
            opacity="0.95"
          />
          {antennaSpin && (
            <g className="antenna-spin" style={{ transformOrigin: `${tipX.toFixed(2)}px ${tipY.toFixed(2)}px` }}>
              <ellipse cx={tipX} cy={tipY} rx="22" ry="8" stroke={C.deep} strokeWidth="1.4" fill="none" opacity="0.5" />
              <ellipse cx={tipX} cy={tipY} rx="26" ry="10" stroke={C.deep} strokeWidth="1.4" fill="none" opacity="0.3" transform={`rotate(-30 ${tipX} ${tipY})`} />
            </g>
          )}

          {/* HEAD: glossy shell → bezel ring → recessed navy screen */}
          <rect x={head.x} y={head.y} width={head.w} height={head.h} rx={head.r} fill={`url(#bodyG-${uid})`} />
          <rect x={bezel.x} y={bezel.y} width={bezel.w} height={bezel.h} rx={bezel.r} fill={bodyDarkFill} />
          <rect x={screen.x} y={screen.y} width={screen.w} height={screen.h} rx={screen.r} fill={`url(#scrG-${uid})`} />

          {cheeks && (
            <>
              <ellipse className="cheek cheek-l" cx={head.x + 22} cy={head.y + head.h - 50} rx="14" ry="9" fill={cheekColor} opacity={cheekOpacity} />
              <ellipse className="cheek cheek-r" cx={head.x + head.w - 22} cy={head.y + head.h - 50} rx="14" ry="9" fill={cheekColor} opacity={cheekOpacity} />
            </>
          )}

          {/* Eyes: soft glow pass under a sharp pass */}
          <g filter={`url(#eglow-${uid})`} opacity="0.75">
            {eyes}
          </g>
          {eyes}

          {tear && (
            <g className="tear">
              <path
                d={`M ${(L.screenCx + L.eyeGapHalf - 4).toFixed(2)} ${(L.screenCy + L.eyeH / 2 + 12).toFixed(2)} q -3 -5 0 -9 q 3 4 0 9 Z`}
                fill={C.blue}
                opacity="0.95"
              />
            </g>
          )}

          {/* Pixel grid + glare over the display (LED look) */}
          <rect x={screen.x} y={screen.y} width={screen.w} height={screen.h} rx={screen.r} fill={`url(#pxg-${uid})`} />
          <ellipse cx={screen.x + screen.w * 0.32} cy={screen.y + 10} rx={screen.w * 0.3} ry="9" fill="#8FB4E8" opacity="0.13" />
          <ellipse cx={head.x + 52} cy={head.y + 16} rx="36" ry="9" fill="#FFFFFF" opacity="0.35" transform={`rotate(-10 ${head.x + 52} ${head.y + 16})`} />

          {/* BODY: glossy shell + chest storage behind a hinged door */}
          <rect x={body.x} y={body.y} width={body.w} height={body.h} rx={body.r} fill={`url(#bodyG-${uid})`} />
          <rect x={chest.x} y={chest.y} width={chest.w} height={chest.h} rx={chest.r} fill="#0B1220" />
          <g className="chest-items" style={{ opacity: 0 }}>
            <path
              d={`M ${chest.x + chest.w * 0.32} ${chest.y + 34} l 6.2 12.7 13.9 2 -10 9.8 2.4 13.9 -12.5 -6.6 -12.5 6.6 2.4 -13.9 -10 -9.8 13.9 -2 Z`}
              fill={C.mustard}
            />
            <g transform={`translate(${chest.x + chest.w * 0.55} ${chest.y + 44})`}>
              <rect x="0" y="0" width="44" height="30" rx="5" fill={C.deep} />
              <circle cx="22" cy="15" r="9" fill="#3A3733" stroke={C.ivory} strokeWidth="1.4" />
              <rect x="31" y="3" width="8" height="5" rx="1.5" fill="#3A3733" />
            </g>
          </g>
          <g className="chest-door" style={{ transformOrigin: `${chest.x + 2}px ${(chest.y + chest.h / 2).toFixed(1)}px` }}>
            <rect x={chest.x} y={chest.y} width={chest.w} height={chest.h} rx={chest.r} fill={`url(#chestG-${uid})`} />
            <line x1={chest.x + 7} y1={chest.y + 12} x2={chest.x + 7} y2={chest.y + chest.h - 12} stroke="#0B1220" strokeWidth="2.4" opacity="0.55" />
            <circle cx={chest.x + chest.w - 16} cy={(chest.y + chest.h / 2).toFixed(1)} r="5" fill={shadeHex(bodyDarkFill, 0.35)} />
          </g>
          <ellipse cx={body.x + 48} cy={body.y + 14} rx="30" ry="7" fill="#FFFFFF" opacity="0.25" transform={`rotate(-8 ${body.x + 48} ${body.y + 14})`} />

          <NovaArms pose={arms} bodyGradientId={`bodyG-${uid}`} bodyGapShrink={bodyGapShrink} />
        </g>
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------------ */

function NovaArms({ pose, bodyGradientId, bodyGapShrink }: { pose: NovaAvatarProps["arms"]; bodyGradientId: string; bodyGapShrink: number }) {
  const { body } = novaLayout(bodyGapShrink);
  const C = NOVA_COLORS;

  if (pose === "float") {
    // Canon: EVE-style detached floating capsules resting at her sides.
    const armW = 30;
    const armH = 96;
    const lax = body.x - armW - 12;
    const rax = body.x + body.w + 12;
    const aay = body.y + 24;
    const capsule = (x: number, side: "l" | "r") => (
      <g className={`float-arm float-arm-${side}`}>
        <rect x={x} y={aay} width={armW} height={armH} rx={15} fill={`url(#${bodyGradientId})`} />
        <ellipse cx={x + 10} cy={aay + 13} rx="7" ry="4" fill="#FFFFFF" opacity="0.32" />
      </g>
    );
    return (
      <>
        {capsule(lax, "l")}
        {capsule(rax, "r")}
      </>
    );
  }

  if (pose === "celebrate") {
    return (
      <>
        <path d={`M ${body.x + 14} ${body.y + 50} Q ${body.x - 18} ${body.y - 10} ${body.x - 6} ${body.y - 60}`} stroke={C.deep} strokeWidth="11" strokeLinecap="round" fill="none" />
        <circle cx={body.x - 6} cy={body.y - 64} r="9" fill={C.deep} />
        <path d={`M ${body.x + body.w - 14} ${body.y + 50} Q ${body.x + body.w + 18} ${body.y - 10} ${body.x + body.w + 6} ${body.y - 60}`} stroke={C.deep} strokeWidth="11" strokeLinecap="round" fill="none" />
        <circle cx={body.x + body.w + 6} cy={body.y - 64} r="9" fill={C.deep} />
      </>
    );
  }

  if (pose === "camera") {
    return (
      <>
        {/* right arm bent up, holding a boxy 1960s film camera */}
        <path d={`M ${body.x + body.w - 14} ${body.y + 60} Q ${body.x + body.w + 24} ${body.y + 10} ${body.x + body.w + 8} ${body.y - 30}`} stroke={C.deep} strokeWidth="11" strokeLinecap="round" fill="none" />
        <g className="camera" transform={`translate(${body.x + body.w - 22} ${body.y - 56})`}>
          <rect x="0" y="0" width="58" height="40" rx="5" fill={C.deep} />
          <rect x="0" y="26" width="58" height="14" fill={C.bodyDark} />
          <circle className="lens-ring" cx="29" cy="20" r="11" fill={C.bodyDark} stroke={C.ivory} strokeWidth="1.4" />
          <circle className="lens-iris" cx="29" cy="20" r="6" fill={C.deep} style={{ transformOrigin: "29px 20px" }} />
          <rect className="lens-flash" x="0" y="0" width="58" height="40" rx="5" fill={C.ivory} opacity="0" />
          <rect x="40" y="4" width="10" height="6" rx="1.5" fill={C.bodyDark} />
        </g>
        {/* left arm small at side */}
        <path d={`M ${body.x + 14} ${body.y + 60} Q ${body.x - 6} ${body.y + 88} ${body.x + 4} ${body.y + 110}`} stroke={C.deep} strokeWidth="11" strokeLinecap="round" fill="none" />
        <circle cx={body.x + 4} cy={body.y + 112} r="8" fill={C.deep} />
      </>
    );
  }

  // "tucked" — persona tiles draw their own prop arms.
  return null;
}

/** Confetti particles — per-particle classes drive independent fall keyframes. */
function Confetti() {
  const C = NOVA_COLORS;
  return (
    <g className="confetti">
      <circle className="conf c1" cx="40" cy="0" r="4" fill={C.mustard} />
      <rect className="conf c2" x="80" y="0" width="6" height="6" fill={C.orange} transform="rotate(20 83 3)" />
      <circle className="conf c3" cx="120" cy="0" r="3.5" fill={C.blue} />
      <path className="conf c4" d="M160 0 l8 0 M164 -4 l0 8" stroke={C.mustard} strokeWidth="4" strokeLinecap="round" />
      <circle className="conf c5" cx="200" cy="0" r="4" fill={C.orange} />
      <rect className="conf c6" x="240" y="0" width="6" height="6" fill={C.mustard} transform="rotate(45 243 3)" />
      <path className="conf c7" d="M280 0 l8 0 M284 -4 l0 8" stroke={C.blue} strokeWidth="3.6" strokeLinecap="round" />
      <circle className="conf c8" cx="320" cy="0" r="3.5" fill={C.orange} />
      <rect className="conf c9" x="60" y="0" width="5" height="5" fill={C.blue} transform="rotate(20 62 2)" />
      <circle className="conf c10" cx="180" cy="0" r="4" fill={C.mustard} />
      <path className="conf c11" d="M260 0 l6 0 M263 -3 l0 6" stroke={C.orange} strokeWidth="3.4" strokeLinecap="round" />
      <circle className="conf c12" cx="340" cy="0" r="3.5" fill={C.blue} />
    </g>
  );
}
